import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Radio,
  Alert,
  Tooltip,
  CircularProgress,
  Collapse,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { initCalendarService, createDeadlineEvents } from '../../utils/microsoftCalendarService';

// Task type/status colors - based on Airtable statuses
const taskTypeColors = {
  // Blocking statuses (full day unavailable)
  'Draaidag': { bg: '#f97316', text: '#fff', blocking: true },
  'Vrij/intern': { bg: '#fbbf24', text: '#000', blocking: true },
  'Vrije dag': { bg: '#fbbf24', text: '#000', blocking: true },
  'Niet beschikbaar': { bg: '#6b7280', text: '#fff', blocking: true },

  // Work statuses
  'Montage': { bg: '#3b82f6', text: '#fff', blocking: false },
  'Animatie': { bg: '#ec4899', text: '#fff', blocking: false },
  'Preproductie': { bg: '#10b981', text: '#fff', blocking: false },
  'Regie': { bg: '#7c3aed', text: '#fff', blocking: false },
  'Edit': { bg: '#3b82f6', text: '#fff', blocking: false },

  // Priority/Other
  'Prio 1': { bg: '#dc2626', text: '#fff', blocking: false },
  'Prio 2': { bg: '#dc2626', text: '#fff', blocking: false },
  'Prio 3': { bg: '#f97316', text: '#fff', blocking: false },
  'Prio 4': { bg: '#ca8a04', text: '#fff', blocking: false },
  'Afspraak': { bg: '#9ca3af', text: '#fff', blocking: false },
  'Vanallesuurtje': { bg: '#60a5fa', text: '#fff', blocking: false },

  // Default
  'default': { bg: '#d1d5db', text: '#374151', blocking: false },
};

// Get task color and blocking type
const getTaskColor = (task) => {
  // Check status first
  if (task.status && taskTypeColors[task.status]) {
    return taskTypeColors[task.status];
  }
  // Check type
  if (task.type && taskTypeColors[task.type]) {
    return taskTypeColors[task.type];
  }
  // Check name for keywords
  const name = (task.name || '').toLowerCase();
  if (name.includes('draaidag') || name.includes('shoot')) {
    return taskTypeColors['Draaidag'];
  }
  if (name.includes('vrij') || name.includes('verlof') || name.includes('vakantie')) {
    return taskTypeColors['Vrije dag'];
  }
  if (name.includes('montage') || name.includes('edit')) {
    return taskTypeColors['Montage'];
  }
  if (name.includes('animatie') || name.includes('motion')) {
    return taskTypeColors['Animatie'];
  }
  return taskTypeColors['default'];
};

// Get blocking type for a task (for capacity display)
const getBlockingType = (task) => {
  const name = (task.name || '').toLowerCase();
  const status = (task.status || '').toLowerCase();

  // Check for draaidag
  if (status.includes('draaidag') || name.includes('draaidag') || name.includes('shoot')) {
    return 'draaidag';
  }
  // Check for vrij/verlof
  if (status.includes('vrij') || status.includes('verlof') ||
      name.includes('vrij') || name.includes('verlof') || name.includes('vakantie')) {
    return 'vrij';
  }
  return null;
};

// Capacity color based on percentage (like Airtable)
// blockingType: 'draaidag', 'vrij', or null
const getCapacityStyle = (percentage, blockingType) => {
  if (blockingType === 'draaidag') {
    return { bg: '#f97316', text: '#fff', label: 'Draai' };
  }
  if (blockingType === 'vrij') {
    return { bg: '#fbbf24', text: '#000', label: 'Vrij' };
  }
  if (percentage === 0) {
    return { bg: '#dcfce7', text: '#166534', label: '0%' };
  }
  if (percentage <= 50) {
    return { bg: '#bbf7d0', text: '#166534', label: `${percentage}%` };
  }
  if (percentage <= 80) {
    return { bg: '#86efac', text: '#166534', label: `${percentage}%` };
  }
  if (percentage <= 100) {
    return { bg: '#fef08a', text: '#854d0e', label: `${percentage}%` };
  }
  if (percentage <= 110) {
    return { bg: '#fca5a5', text: '#991b1b', label: `${percentage}%` };
  }
  return { bg: '#dc2626', text: '#fff', label: `${percentage}%` };
};

// Role colors for new tasks bar
const roleColors = {
  'Editor': '#3b82f6',
  'Animator': '#ec4899',
  'Illustrator': '#f97316',
  'Projectleider': '#10b981',
  'Copywriter': '#8b5cf6',
  'Regisseur': '#7c3aed',
  'Creative': '#a855f7',
};

// Generate days array between start and end date (working days only)
const generateWorkingDays = (startDate, endDate) => {
  const days = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  const dayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
  const monthNames = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

  while (current <= end) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Skip weekends
      days.push({
        date: new Date(current),
        iso: current.toISOString().split('T')[0],
        dayOfWeek,
        dayName: dayNames[dayOfWeek],
        dayNum: current.getDate(),
        month: monthNames[current.getMonth()],
        isFirstOfMonth: current.getDate() === 1 || days.length === 0,
      });
    }
    current.setDate(current.getDate() + 1);
  }

  return days;
};

// Calculate hours per day for a task
const getTaskDaysMap = (task, days) => {
  const startIso = task.startDate;
  const endIso = task.endDate || task.startDate;
  const result = {};

  days.forEach(day => {
    if (day.iso >= startIso && day.iso <= endIso) {
      result[day.iso] = true;
    }
  });

  return result;
};

// Detect recurring appointments (weekly meetings, standups, etc.)
const isRecurringAppointment = (task) => {
  const name = (task.name || '').toLowerCase();
  const recurringKeywords = [
    'standup', 'stand-up', 'daily', 'weekly', 'wekelijks',
    'meeting', 'overleg', 'sync', 'check-in', 'checkin',
    'retro', 'retrospective', 'sprint', 'scrum',
    'teamoverleg', 'vergadering',
  ];
  return recurringKeywords.some(kw => name.includes(kw));
};

// Group tasks by type for display (e.g., all Vanallesuurtje on one line, small tasks grouped, recurring appointments)
const groupTasksForDisplay = (tasks, workingDays) => {
  const groupableTypes = ['vanallesuurtje'];
  const SMALL_TASK_THRESHOLD = 0.5; // Hours threshold for "small tasks"

  const grouped = [];
  const groupedTasks = {};
  const smallTasks = [];
  const recurringTasks = {};

  tasks.forEach(task => {
    const name = (task.name || '').toLowerCase();
    const hours = task.hours || 0;

    // Check if it's a groupable type (like Vanallesuurtje)
    const isGroupable = groupableTypes.some(type => name.includes(type));

    // Check if recurring appointment
    const isRecurring = isRecurringAppointment(task);

    // Check if it's a small task (less than threshold hours)
    const isSmallTask = hours > 0 && hours < SMALL_TASK_THRESHOLD;

    if (isGroupable) {
      const groupKey = groupableTypes.find(type => name.includes(type)) || 'other';
      if (!groupedTasks[groupKey]) {
        groupedTasks[groupKey] = {
          id: `group-${groupKey}`,
          name: groupKey.charAt(0).toUpperCase() + groupKey.slice(1),
          isGroup: true,
          tasks: [],
          status: task.status,
        };
      }
      groupedTasks[groupKey].tasks.push(task);
    } else if (isRecurring) {
      // Group recurring by normalized name (e.g., "weekly meeting" -> "meeting")
      const baseName = name.replace(/wekelijks|weekly|dagelijks|daily/gi, '').trim();
      const groupKey = baseName || 'recurring';
      if (!recurringTasks[groupKey]) {
        recurringTasks[groupKey] = {
          id: `group-recurring-${groupKey}`,
          name: `${task.name} (herhalend)`,
          isGroup: true,
          tasks: [],
          status: task.status || 'Afspraak',
        };
      }
      recurringTasks[groupKey].tasks.push(task);
    } else if (isSmallTask) {
      smallTasks.push(task);
    } else {
      grouped.push(task);
    }
  });

  // Add grouped tasks
  Object.values(groupedTasks).forEach(group => {
    grouped.push(group);
  });

  // Add recurring appointment groups
  Object.values(recurringTasks).forEach(group => {
    grouped.push(group);
  });

  // Add small tasks as a group if there are any
  if (smallTasks.length > 0) {
    grouped.push({
      id: 'group-small',
      name: `Kleine taken (${smallTasks.length}x)`,
      isGroup: true,
      tasks: smallTasks,
      status: '',
    });
  }

  return grouped;
};

// Check if two tasks are versions that need feedback time between them
const needsFeedbackBetween = (taskA, taskB) => {
  if (!taskA || !taskB) return false;

  const nameA = (taskA.name || '').toLowerCase();
  const nameB = (taskB.name || '').toLowerCase();

  // Extract base name (e.g., "montage" from "montage v1" or "montage def")
  const getBaseName = (name) => {
    return name.replace(/\s*(v\s*\d+|v\s*intern|versie\s*\d+|intern|def|definitief|final).*$/i, '').trim();
  };

  const baseA = getBaseName(nameA);
  const baseB = getBaseName(nameB);

  // Only add feedback if same base type
  if (baseA !== baseB || !baseA) return false;

  // Check if it's a version transition
  const versionPatterns = [
    /v\s*intern/i,
    /v\s*1/i, /v1/i,
    /v\s*2/i, /v2/i,
    /v\s*3/i, /v3/i,
    /versie\s*\d+/i,
    /intern/i,
    /\bdef\b/i,
    /definitief/i,
    /final/i,
  ];

  const hasVersionA = versionPatterns.some(p => p.test(nameA));
  const hasVersionB = versionPatterns.some(p => p.test(nameB));

  return hasVersionA && hasVersionB;
};

function PlanningPuzzleDialog({
  open,
  onClose,
  onApply,
  startDate,
  endDate,
  tasks = [],
  capacityData = {},
  personen = [],
  deliverableName = 'Deliverable',
  defaultFeedbackDays = 3,
  projectName = '',
}) {
  const { instance } = useMsal();
  const [calendarLoading, setCalendarLoading] = useState(false);

  // Selected person per role
  const [assignments, setAssignments] = useState({});

  // Existing tasks per person
  const [existingTasks, setExistingTasks] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Expanded persons (to see their tasks)
  const [expandedPersons, setExpandedPersons] = useState({});

  // Feedback days between versions (configurable)
  const [feedbackDays, setFeedbackDays] = useState(defaultFeedbackDays);

  // Planning offset (to shift the entire planning)
  const [planningOffset, setPlanningOffset] = useState(0);

  // Task duration overrides (for adjusting individual task lengths)
  const [taskDurationOverrides, setTaskDurationOverrides] = useState({});

  // Per-task assignee overrides (taskId -> personId) — allows different person per version
  const [taskAssignments, setTaskAssignments] = useState({});

  // Drag state (resize or move)
  const dragRef = useRef(null); // { taskId, type: 'resize'|'move', startX, startDuration/startOffset }
  const timelineRef = useRef(null);

  // Per-task offset overrides (taskId -> offset in days, for moving individual tasks)
  const [taskOffsets, setTaskOffsets] = useState({});

  // Per-task hours overrides (taskId -> hours, passed back via handleApply)
  const [taskHoursOverrides, setTaskHoursOverrides] = useState({});

  // Generate working days for the timeline (extend 7 working days past deadline)
  const workingDays = useMemo(() => {
    const extendedEnd = new Date(endDate);
    let addedDays = 0;
    while (addedDays < 7) {
      extendedEnd.setDate(extendedEnd.getDate() + 1);
      const dow = extendedEnd.getDay();
      if (dow !== 0 && dow !== 6) addedDays++;
    }
    return generateWorkingDays(startDate, extendedEnd.toISOString().split('T')[0]);
  }, [startDate, endDate]);
  const totalDays = workingDays.length;
  const HOURS_PER_DAY = 8;

  // Laatste kolom die nog op/vóór de deadline valt. De timeline loopt bewust 7
  // werkdagen door — zonder markering zie je niet dat je eroverheen plant.
  const deadlineIdx = useMemo(() => {
    const deadlineIso = String(endDate).split('T')[0];
    let last = -1;
    workingDays.forEach((day, idx) => { if (day.iso <= deadlineIso) last = idx; });
    return last;
  }, [workingDays, endDate]);

  // Get unique roles from tasks
  const roles = useMemo(() => {
    const roleSet = new Set(tasks.map(t => t.type).filter(Boolean));
    return [...roleSet];
  }, [tasks]);

  // Get all person IDs from capacityData
  const allPersonIds = useMemo(() => {
    const ids = new Set();
    Object.values(capacityData).forEach(persons => {
      persons.forEach(p => ids.add(p.id));
    });
    return [...ids];
  }, [capacityData]);

  // Fetch existing tasks when dialog opens
  useEffect(() => {
    if (open && allPersonIds.length > 0 && startDate && endDate) {
      fetchExistingTasks();
    }
  }, [open, allPersonIds, startDate, endDate]);

  const fetchExistingTasks = async () => {
    setLoadingTasks(true);
    try {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      allPersonIds.forEach(id => params.append('personIds[]', id));

      const response = await authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`);
      if (response.ok) {
        const data = await response.json();
        setExistingTasks(data);
      }
    } catch (err) {
      console.error('Error fetching existing tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Calculate capacity per day for each person
  const personCapacityPerDay = useMemo(() => {
    const result = {};

    allPersonIds.forEach(personId => {
      result[personId] = {};
      const personTasks = existingTasks[personId] || [];

      workingDays.forEach(day => {
        let totalHours = 0;
        let blockingType = null; // 'draaidag', 'vrij', or null
        const tasksOnDay = [];

        personTasks.forEach(task => {
          const taskDays = getTaskDaysMap(task, workingDays);
          if (taskDays[day.iso]) {
            // Calculate hours for this day
            const taskWorkingDays = Object.keys(taskDays).length;
            const hoursPerDay = (task.hours || 8) / Math.max(1, taskWorkingDays);
            totalHours += hoursPerDay;
            tasksOnDay.push(task);

            // Check if blocking (draaidag takes priority over vrij)
            const taskBlockingType = getBlockingType(task);
            if (taskBlockingType === 'draaidag') {
              blockingType = 'draaidag';
            } else if (taskBlockingType === 'vrij' && blockingType !== 'draaidag') {
              blockingType = 'vrij';
            }
          }
        });

        const percentage = Math.round((totalHours / HOURS_PER_DAY) * 100);
        result[personId][day.iso] = {
          hours: totalHours,
          percentage,
          blockingType,
          tasks: tasksOnDay,
        };
      });
    });

    return result;
  }, [allPersonIds, existingTasks, workingDays, HOURS_PER_DAY]);

  // Calculate new task positions (for the top bar) with feedback gaps and offset
  const newTaskPositions = useMemo(() => {
    let currentDayIndex = Math.max(0, planningOffset); // Apply global offset
    const positions = [];

    tasks.forEach((task, idx) => {
      const prevTask = idx > 0 ? tasks[idx - 1] : null;

      // Add feedback gap if needed between versions
      if (needsFeedbackBetween(prevTask, task) && feedbackDays > 0) {
        positions.push({
          id: `feedback-${idx}`,
          name: 'Feedback',
          type: 'Feedback',
          isFeedbackGap: true,
          startIdx: currentDayIndex,
          duration: feedbackDays,
          left: (currentDayIndex / totalDays) * 100,
          width: Math.max(2, (feedbackDays / totalDays) * 100),
        });
        currentDayIndex += feedbackDays;
      }

      // Apply per-task offset (individual shift)
      const taskOffset = taskOffsets[task.id] || 0;
      const startIdx = Math.max(0, currentDayIndex + taskOffset);

      // Use override duration if set, otherwise use task.days
      const baseDuration = task.days || 1;
      const duration = taskDurationOverrides[task.id] !== undefined
        ? taskDurationOverrides[task.id]
        : baseDuration;
      // Next task starts after this one (using non-offset position to keep chain)
      currentDayIndex += duration;

      positions.push({
        ...task,
        startIdx,
        duration,
        baseDuration,
        taskOffset,
        left: (startIdx / totalDays) * 100,
        width: Math.max(3, (duration / totalDays) * 100),
      });
    });

    return positions;
  }, [tasks, totalDays, feedbackDays, planningOffset, taskDurationOverrides, taskOffsets]);

  // Loopt de planning voorbij de deadline? (laatste bezette kolom vs deadline-kolom)
  const deadlineOverschrijding = useMemo(() => {
    if (deadlineIdx < 0 || newTaskPositions.length === 0) return null;
    const laatsteIdx = Math.max(...newTaskPositions.map(p => p.startIdx + p.duration - 1));
    if (laatsteIdx <= deadlineIdx) return null;
    const laatsteDag = workingDays[Math.min(laatsteIdx, workingDays.length - 1)];
    return {
      dagen: laatsteIdx - deadlineIdx,
      eindDatum: laatsteDag?.iso,
    };
  }, [newTaskPositions, deadlineIdx, workingDays]);

  // Reset task duration to original
  const resetTaskDuration = (taskId) => {
    setTaskDurationOverrides(prev => {
      const newOverrides = { ...prev };
      delete newOverrides[taskId];
      return newOverrides;
    });
  };

  // Drag handlers for both resize and move
  const handleDragStart = useCallback((e, taskId, dragType) => {
    e.preventDefault();
    e.stopPropagation();
    const task = newTaskPositions.find(t => t.id === taskId);
    if (!task) return;

    dragRef.current = {
      taskId,
      type: dragType, // 'resize' or 'move'
      startX: e.clientX,
      startDuration: task.duration,
      startOffset: taskOffsets[taskId] || 0,
    };

    const handleMouseMove = (moveEvent) => {
      if (!dragRef.current || !timelineRef.current) return;
      const { taskId, type, startDuration, startOffset } = dragRef.current;
      const timelineRect = timelineRef.current.getBoundingClientRect();
      const pixelsPerDay = timelineRect.width / totalDays;
      const deltaX = moveEvent.clientX - dragRef.current.startX;
      const deltaDays = Math.round(deltaX / pixelsPerDay);

      if (type === 'resize') {
        const newDuration = Math.max(1, startDuration + deltaDays);
        setTaskDurationOverrides(prev => ({ ...prev, [taskId]: newDuration }));
      } else if (type === 'move') {
        const newOffset = startOffset + deltaDays;
        setTaskOffsets(prev => ({ ...prev, [taskId]: newOffset }));
      } else if (type === 'resize-left') {
        const newOffset = startOffset + deltaDays;
        const newDuration = Math.max(1, startDuration - deltaDays);
        setTaskOffsets(prev => ({ ...prev, [taskId]: newOffset }));
        setTaskDurationOverrides(prev => ({ ...prev, [taskId]: newDuration }));
      }
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = (dragType === 'resize' || dragType === 'resize-left') ? 'ew-resize' : 'grab';
    document.body.style.userSelect = 'none';
  }, [newTaskPositions, totalDays, taskOffsets]);

  // Calculate which days each new task occupies, mapped per personId
  const newTaskDayMap = useMemo(() => {
    const mapByRole = {}; // { role: { dayIso: hours } } — legacy, for role-level display
    const mapByPerson = {}; // { personId: { dayIso: hours } } — for per-person display

    newTaskPositions.forEach(task => {
      if (task.isFeedbackGap) return;

      const role = task.type;
      if (!mapByRole[role]) mapByRole[role] = {};

      const totalTaskHours = task.hours > 0 ? task.hours : (task.duration * 8);
      const hoursPerDay = totalTaskHours / Math.max(1, task.duration);

      // Determine effective assignee for this task
      const assigneeId = taskAssignments[task.id] || assignments[role] || null;

      for (let i = 0; i < task.duration; i++) {
        const dayIdx = task.startIdx + i;
        if (dayIdx < workingDays.length) {
          const dayIso = workingDays[dayIdx]?.iso;
          if (dayIso) {
            mapByRole[role][dayIso] = (mapByRole[role][dayIso] || 0) + hoursPerDay;
            if (assigneeId) {
              if (!mapByPerson[assigneeId]) mapByPerson[assigneeId] = {};
              mapByPerson[assigneeId][dayIso] = (mapByPerson[assigneeId][dayIso] || 0) + hoursPerDay;
            }
          }
        }
      }
    });

    return { byRole: mapByRole, byPerson: mapByPerson };
  }, [newTaskPositions, workingDays, assignments, taskAssignments]);

  // Calculate combined capacity (existing + new) for selected persons
  const getCombinedCapacity = (personId, role, dayIso) => {
    const existingData = personCapacityPerDay[personId]?.[dayIso] || { hours: 0, blockingType: null };

    // Check if this person is assigned to ANY task (role-level or per-task)
    const isRoleSelected = assignments[role] === personId;
    const hasAnyTaskAssignment = Object.values(taskAssignments).includes(personId);
    const isSelected = isRoleSelected || hasAnyTaskAssignment;

    if (!isSelected) {
      return existingData;
    }

    // Add new task hours for this person (using per-person map)
    const newHours = newTaskDayMap.byPerson?.[personId]?.[dayIso] || 0;
    const combinedHours = existingData.hours + newHours;
    const percentage = Math.round((combinedHours / HOURS_PER_DAY) * 100);

    return {
      ...existingData,
      hours: combinedHours,
      percentage,
      newHours, // Track how much is new
      hasNewTasks: newHours > 0,
    };
  };

  // Calculate overall capacity percentage for a person (existing + new tasks assigned)
  const getOverallCapacity = (personId, role) => {
    // Sum existing hours over the period
    const existingData = personCapacityPerDay[personId] || {};
    let existingHours = 0;
    workingDays.forEach(day => {
      existingHours += existingData[day.iso]?.hours || 0;
    });

    // Check if this person is assigned to any new tasks
    const isRoleSelected = assignments[role] === personId;
    const hasAnyTaskAssignment = Object.values(taskAssignments).includes(personId);
    const isSelected = isRoleSelected || hasAnyTaskAssignment;

    // Sum new task hours if selected
    let newHours = 0;
    if (isSelected) {
      const personNewTasks = newTaskDayMap.byPerson?.[personId] || {};
      Object.values(personNewTasks).forEach(hours => {
        newHours += hours;
      });
    }

    const totalHours = existingHours + newHours;
    const totalAvailableHours = workingDays.length * HOURS_PER_DAY;
    return totalAvailableHours > 0 ? Math.round((totalHours / totalAvailableHours) * 100) : 0;
  };

  // Handle person selection (role-level default)
  const handleSelectPerson = (role, personId) => {
    setAssignments(prev => ({
      ...prev,
      [role]: prev[role] === personId ? null : personId,
    }));
  };

  // Handle per-task person assignment (overrides role default)
  const handleTaskAssignment = (taskId, personId) => {
    setTaskAssignments(prev => {
      const newAssignments = { ...prev };
      if (newAssignments[taskId] === personId) {
        delete newAssignments[taskId]; // Remove override, fall back to role default
      } else {
        newAssignments[taskId] = personId;
      }
      return newAssignments;
    });
  };

  // Get effective assignee for a task (per-task override or role default)
  // Returns '__UNASSIGNED__' if explicitly set to "nog te plannen", personId otherwise
  const getTaskAssignee = (task) => {
    if (taskAssignments[task.id] !== undefined) return taskAssignments[task.id];
    return assignments[task.type] || null;
  };

  // Toggle person expansion
  const togglePersonExpanded = (personId) => {
    setExpandedPersons(prev => ({
      ...prev,
      [personId]: !prev[personId],
    }));
  };

  // Handle apply - returns role-level assignments, per-task assignments, AND puzzle adjustments
  const handleAddToCalendar = async () => {
    const account = instance.getActiveAccount();
    if (!account) {
      toast.error('Log in met Microsoft om agenda-events aan te maken');
      return;
    }
    initCalendarService(instance, account);

    // Build deadline event from endDate + deliverableName
    const deadlineDate = endDate instanceof Date ? endDate.toISOString().split('T')[0] : String(endDate).split('T')[0];
    const deadlineEvents = [{
      name: deliverableName,
      deadline: deadlineDate,
      projectName: projectName || 'Project',
      deadlineType: 'Klant',
    }];

    setCalendarLoading(true);
    try {
      const result = await createDeadlineEvents(deadlineEvents);
      if (result.created.length > 0) {
        toast.success(`Deadline "${deliverableName}" in Outlook gezet (${deadlineDate} om 15:00)`);
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} event(s) mislukt`);
      }
    } catch (err) {
      toast.error('Agenda-event aanmaken mislukt');
      console.error('[Calendar] Error:', err);
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleApply = () => {
    const result = {};
    // Role-level assignments
    Object.entries(assignments).forEach(([role, personId]) => {
      if (personId && personId !== '__UNASSIGNED__') {
        const person = personen.find(p => p.id === personId);
        if (person) {
          result[role] = person;
        }
      }
    });
    // Per-task overrides (including "nog te plannen" = null)
    const taskOverrides = {};
    Object.entries(taskAssignments).forEach(([taskId, personId]) => {
      if (personId === '__UNASSIGNED__') {
        taskOverrides[taskId] = null; // Explicitly unassigned
      } else if (personId) {
        const person = personen.find(p => p.id === personId);
        if (person) {
          taskOverrides[taskId] = person;
        }
      }
    });
    result._taskAssignments = taskOverrides;

    // NEW: Pass puzzle adjustments (duration, offset, feedback, hours)
    result._taskDurationOverrides = taskDurationOverrides;
    result._taskOffsets = taskOffsets;
    result._feedbackDays = feedbackDays;
    result._planningOffset = planningOffset;
    result._taskHoursOverrides = taskHoursOverrides;

    onApply(result);
  };

  // Check if all tasks have assignments (either role-level or per-task)
  // Note: '__UNASSIGNED__' counts as "assigned" (explicitly marked for later planning)
  const allAssigned = tasks.every(t => {
    const assignee = getTaskAssignee(t);
    return assignee !== null && assignee !== undefined;
  });

  // Group new tasks by type for display on separate rows
  const tasksByType = useMemo(() => {
    const grouped = {};
    const feedbackGaps = [];

    newTaskPositions.forEach((task, idx) => {
      if (task.isFeedbackGap) {
        feedbackGaps.push(task);
        return;
      }
      const type = task.type || 'Overig';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(task);
    });

    return { grouped, feedbackGaps };
  }, [newTaskPositions]);

  // Day column width
  const dayWidth = 44;
  const sidebarWidth = 140;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, minHeight: 600, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CalendarViewWeekIcon sx={{ color: '#3b82f6' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Planning Puzzel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deliverableName}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Period info + controls */}
        <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Periode: <strong>{new Date(startDate).toLocaleDateString('nl-NL')}</strong> t/m{' '}
            <strong>{new Date(endDate).toLocaleDateString('nl-NL')}</strong>
            {' '}({totalDays} werkdagen)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Planning shift controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Start:</Typography>
              <IconButton
                size="small"
                onClick={() => setPlanningOffset(Math.max(0, planningOffset - 1))}
                disabled={planningOffset <= 0}
                sx={{ p: 0.25 }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Chip
                label={`dag ${planningOffset + 1}`}
                size="small"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
              <IconButton
                size="small"
                onClick={() => setPlanningOffset(planningOffset + 1)}
                sx={{ p: 0.25 }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Feedback days control */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                FB:
              </Typography>
              <TextField
                type="number"
                size="small"
                value={feedbackDays}
                onChange={(e) => setFeedbackDays(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 10, style: { width: 32, textAlign: 'center', padding: '4px' } }}
                sx={{ '& .MuiOutlinedInput-root': { height: 24 } }}
              />
              <Typography variant="caption" color="text.secondary">d</Typography>
            </Box>
          </Box>
        </Box>

        {deadlineOverschrijding && (
          <Box sx={{ px: 3, py: 1, backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
            <Typography variant="body2" sx={{ color: '#b91c1c', fontWeight: 600 }}>
              ⚠️ Planning loopt {deadlineOverschrijding.dagen} werkdag(en) ná de deadline
              {deadlineOverschrijding.eindDatum ? ` (tot ${new Date(deadlineOverschrijding.eindDatum).toLocaleDateString('nl-NL')})` : ''}.
              Verkort taken of start eerder.
            </Typography>
          </Box>
        )}

        {loadingTasks && (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Bestaande taken laden...</Typography>
          </Box>
        )}

        {/* Main scrollable content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Sticky planning section (header + tasks + assignment row) */}
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}>
          {/* Timeline header with days */}
          <Box sx={{
            display: 'flex',
            borderBottom: '2px solid #e5e7eb',
            backgroundColor: '#fff',
          }}>
            <Box sx={{ width: sidebarWidth, minWidth: sidebarWidth, borderRight: '1px solid #e5e7eb', p: 1, backgroundColor: '#f9fafb' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">PLANNING</Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              {workingDays.map((day, idx) => (
                <Box
                  key={day.iso}
                  sx={{
                    width: dayWidth,
                    minWidth: dayWidth,
                    textAlign: 'center',
                    py: 0.5,
                    borderRight: '1px solid #f3f4f6',
                    borderLeft: deadlineIdx >= 0 && idx === deadlineIdx + 1
                      ? '2px solid #ef4444'
                      : (day.isFirstOfMonth && idx > 0 ? '2px solid #3b82f6' : 'none'),
                    backgroundColor: deadlineIdx >= 0 && idx > deadlineIdx ? '#fef2f2' : 'transparent',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                    {day.dayName}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                    {day.dayNum}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* NEW TASKS - Grouped by type (Editor, Animator, etc.) */}
          {Object.entries(tasksByType.grouped).map(([type, typeTasks]) => (
            <Box key={type} sx={{ display: 'flex', borderBottom: '1px solid #3b82f6', backgroundColor: '#eff6ff' }}>
              <Box sx={{
                width: sidebarWidth,
                minWidth: sidebarWidth,
                borderRight: '1px solid #e5e7eb',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}>
                <Box sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: roleColors[type] || '#3b82f6',
                }} />
                <Typography variant="caption" fontWeight={700} sx={{ color: roleColors[type] || '#3b82f6' }}>
                  {type}
                </Typography>
              </Box>
              <Box ref={timelineRef} sx={{ flex: 1, position: 'relative', minHeight: 40, display: 'flex' }}>
                {/* Day columns background */}
                {workingDays.map(day => (
                  <Box key={day.iso} sx={{ width: dayWidth, minWidth: dayWidth, borderRight: '1px solid #e0e7ff' }} />
                ))}
                {/* Task bars for this type */}
                {typeTasks.map((task, idx) => {
                  const isOverridden = taskDurationOverrides[task.id] !== undefined;
                  const isMoved = (taskOffsets[task.id] || 0) !== 0;

                  return (
                    <Tooltip
                      key={task.id || idx}
                      title={
                        <Box>
                          <Typography variant="caption" display="block" fontWeight={600}>{task.name}</Typography>
                          <Typography variant="caption" display="block">
                            {task.duration}d {task.hours ? `/ ${task.hours}u` : ''}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#a5b4fc' }}>
                            {isOverridden ? 'Dubbelklik = reset' : 'Sleep rand = pas duur aan'}
                          </Typography>
                        </Box>
                      }
                      arrow
                    >
                      <Box
                        onMouseDown={(e) => {
                          handleDragStart(e, task.id, 'move');
                        }}
                        onDoubleClick={() => {
                          // Reset duration and offset on double-click
                          if (isOverridden) resetTaskDuration(task.id);
                          if (isMoved) setTaskOffsets(prev => { const n = { ...prev }; delete n[task.id]; return n; });
                        }}
                        sx={{
                          position: 'absolute',
                          left: `${task.left}%`,
                          width: `${task.width}%`,
                          top: 4,
                          height: 28,
                          backgroundColor: roleColors[task.type] || '#3b82f6',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.5,
                          boxShadow: (isOverridden || isMoved)
                            ? '0 0 0 2px #f59e0b, 0 2px 6px rgba(0,0,0,0.2)'
                            : '0 2px 4px rgba(0,0,0,0.15)',
                          border: (isOverridden || isMoved) ? '2px solid #f59e0b' : '2px solid rgba(255,255,255,0.5)',
                          overflow: 'visible',
                          cursor: 'grab',
                          transition: 'box-shadow 0.15s, border-color 0.15s',
                          '&:active': { cursor: 'grabbing' },
                        }}
                      >
                        {/* Task label */}
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.55rem',
                            lineHeight: 1.2,
                            textAlign: 'center',
                            pointerEvents: 'none',
                          }}
                        >
                          {task.name}
                          {task.hours > 0 && (
                            <Box component="span" sx={{ opacity: 0.85, ml: 0.3 }}>
                              ({task.hours}u)
                            </Box>
                          )}
                        </Typography>

                        {/* Left drag handle for resize */}
                        <Box
                          onMouseDown={(e) => { e.stopPropagation(); handleDragStart(e, task.id, 'resize-left'); }}
                          sx={{
                            position: 'absolute',
                            left: -4,
                            top: 0,
                            bottom: 0,
                            width: 8,
                            cursor: 'ew-resize',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              '& .drag-indicator-left': {
                                opacity: 1,
                                backgroundColor: 'rgba(255,255,255,0.9)',
                              },
                            },
                          }}
                        >
                          <Box
                            className="drag-indicator-left"
                            sx={{
                              width: 4,
                              height: 16,
                              borderRadius: 2,
                              backgroundColor: 'rgba(255,255,255,0.5)',
                              opacity: 0.6,
                              transition: 'opacity 0.15s, background-color 0.15s',
                            }}
                          />
                        </Box>

                        {/* Right drag handle for resize */}
                        <Box
                          onMouseDown={(e) => { e.stopPropagation(); handleDragStart(e, task.id, 'resize'); }}
                          sx={{
                            position: 'absolute',
                            right: -4,
                            top: 0,
                            bottom: 0,
                            width: 8,
                            cursor: 'ew-resize',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              '& .drag-indicator': {
                                opacity: 1,
                                backgroundColor: 'rgba(255,255,255,0.9)',
                              },
                            },
                          }}
                        >
                          <Box
                            className="drag-indicator"
                            sx={{
                              width: 4,
                              height: 16,
                              borderRadius: 2,
                              backgroundColor: 'rgba(255,255,255,0.5)',
                              opacity: 0.6,
                              transition: 'opacity 0.15s, background-color 0.15s',
                            }}
                          />
                        </Box>

                        {/* Badge (shown when overridden or moved) */}
                        {(isOverridden || isMoved) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -4,
                              backgroundColor: '#f59e0b',
                              color: '#fff',
                              borderRadius: '50%',
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.5rem',
                              fontWeight: 700,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }}
                          >
                            {task.duration}d
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
                {/* Feedback gaps on first type row only */}
                {type === Object.keys(tasksByType.grouped)[0] && tasksByType.feedbackGaps.map((task, idx) => (
                  <Tooltip key={task.id || `fb-${idx}`} title={`Feedback ronde (${task.duration}d)`} arrow>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${task.left}%`,
                        width: `${task.width}%`,
                        top: 14,
                        height: 12,
                        borderTop: '1px dashed #9ca3af',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.5rem' }}>
                        FB
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}


          {/* Per-task assignment row */}
          <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e7ff', backgroundColor: '#f8faff' }}>
            <Box sx={{
              width: sidebarWidth,
              minWidth: sidebarWidth,
              borderRight: '1px solid #e5e7eb',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#6b7280' }}>
                Uitvoerder
              </Typography>
            </Box>
            <Box ref={timelineRef} sx={{ flex: 1, position: 'relative', minHeight: 24, display: 'flex' }}>
              {workingDays.map(day => (
                <Box key={`assign-${day.iso}`} sx={{ width: dayWidth, minWidth: dayWidth, borderRight: '1px solid #f0f0f5' }} />
              ))}
              {newTaskPositions.filter(t => !t.isFeedbackGap).map((task, idx) => {
                const assigneeId = getTaskAssignee(task);
                const hasTaskOverride = !!taskAssignments[task.id];
                const isUnassigned = assigneeId === '__UNASSIGNED__' || taskAssignments[task.id] === '__UNASSIGNED__';
                const assigneePerson = (assigneeId && assigneeId !== '__UNASSIGNED__') ? personen.find(p => p.id === assigneeId) : null;
                const rolePersons = capacityData[task.type] || [];
                const displayName = assigneePerson
                  ? (assigneePerson.firstName || assigneePerson.name || '').split(' ')[0]
                  : '?';

                return (
                  <Tooltip
                    key={`assign-${task.id}`}
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" fontWeight={600} display="block">
                          {task.name} — Kies uitvoerder:
                        </Typography>
                        {rolePersons.map(p => (
                          <Typography
                            key={p.id}
                            variant="caption"
                            display="block"
                            sx={{
                              cursor: 'pointer',
                              fontWeight: getTaskAssignee(task) === p.id ? 700 : 400,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => handleTaskAssignment(task.id, p.id)}
                          >
                            {getTaskAssignee(task) === p.id ? '● ' : '○ '}
                            {p.firstName || p.name}
                          </Typography>
                        ))}
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{
                            cursor: 'pointer',
                            color: '#6b7280',
                            fontStyle: 'italic',
                            mt: 0.5,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={() => handleTaskAssignment(task.id, '__UNASSIGNED__')}
                        >
                          ○ Nog te plannen
                        </Typography>
                        {hasTaskOverride && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 0.5, cursor: 'pointer', color: '#f59e0b', fontStyle: 'italic' }}
                            onClick={() => handleTaskAssignment(task.id, taskAssignments[task.id])}
                          >
                            ↩ Terug naar rol-standaard
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                    placement="bottom"
                    enterDelay={0}
                    leaveDelay={200}
                    PopperProps={{ sx: { '& .MuiTooltip-tooltip': { maxWidth: 220 } } }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${task.left}%`,
                        width: `${task.width}%`,
                        top: 2,
                        height: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <Chip
                        label={isUnassigned ? 'Te plannen' : (assigneePerson ? displayName : '—')}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: '0.55rem',
                          fontWeight: hasTaskOverride ? 700 : 500,
                          maxWidth: '100%',
                          backgroundColor: isUnassigned ? '#fef3c7' : hasTaskOverride ? '#fef3c7' : assigneePerson ? '#e0e7ff' : '#f3f4f6',
                          color: isUnassigned ? '#92400e' : hasTaskOverride ? '#b45309' : assigneePerson ? '#3730a3' : '#9ca3af',
                          border: isUnassigned ? '1px dashed #f59e0b' : hasTaskOverride ? '1px solid #f59e0b' : 'none',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
          </Box>
          {/* End sticky planning section */}

          {/* Roles and persons */}
          {roles.map(role => {
            const roleCapacity = capacityData[role] || [];
            const selectedPersonId = assignments[role];

            return (
              <Box key={role}>
                {/* Role header */}
                <Box sx={{
                  display: 'flex',
                  backgroundColor: '#f3f4f6',
                  borderBottom: '1px solid #e5e7eb',
                }}>
                  <Box sx={{
                    width: sidebarWidth,
                    minWidth: sidebarWidth,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Box sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: roleColors[role] || '#6b7280',
                    }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                      {role}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                </Box>

                {/* Persons for this role */}
                {roleCapacity.length > 0 ? (
                  roleCapacity.map(person => {
                    const isSelected = selectedPersonId === person.id;
                    // Calculate overall capacity dynamically (includes new tasks if selected)
                    const overallPercentage = getOverallCapacity(person.id, role);
                    const personDayCapacity = personCapacityPerDay[person.id] || {};
                    const personTasks = existingTasks[person.id] || [];
                    const isExpanded = expandedPersons[person.id] || false;

                    return (
                      <Box key={person.id}>
                        {/* Person row with percentages per day */}
                        <Box
                          sx={{
                            display: 'flex',
                            borderBottom: '1px solid #f3f4f6',
                            backgroundColor: isSelected ? '#eff6ff' : '#fff',
                            transition: 'background-color 0.15s',
                            '&:hover': { backgroundColor: isSelected ? '#eff6ff' : '#f9fafb' },
                          }}
                        >
                          {/* Person info sidebar */}
                          <Box
                            sx={{
                              width: sidebarWidth,
                              minWidth: sidebarWidth,
                              borderRight: '1px solid #e5e7eb',
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            {/* Expand arrow */}
                            <IconButton
                              size="small"
                              onClick={() => togglePersonExpanded(person.id)}
                              sx={{ p: 0.25 }}
                            >
                              {isExpanded ? (
                                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>

                            {/* Radio button */}
                            <Radio
                              checked={isSelected}
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={() => handleSelectPerson(role, person.id)}
                            />

                            {/* Name */}
                            <Typography
                              variant="body2"
                              fontWeight={isSelected ? 600 : 400}
                              noWrap
                              sx={{ flex: 1, fontSize: '0.75rem', cursor: 'pointer' }}
                              onClick={() => handleSelectPerson(role, person.id)}
                            >
                              {person.firstName || person.name}
                            </Typography>

                            {/* Overall percentage chip */}
                            <Chip
                              label={`${overallPercentage}%`}
                              size="small"
                              sx={{
                                height: 18,
                                minWidth: 36,
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                ...(() => {
                                  const style = getCapacityStyle(overallPercentage, false);
                                  return { backgroundColor: style.bg, color: style.text };
                                })(),
                              }}
                            />
                          </Box>

                          {/* Day cells with percentages */}
                          <Box sx={{ display: 'flex' }}>
                            {workingDays.map(day => {
                              const existingData = personDayCapacity[day.iso] || { percentage: 0, blockingType: null, hours: 0, tasks: [] };
                              const combinedData = getCombinedCapacity(person.id, role, day.iso);
                              const style = getCapacityStyle(combinedData.percentage, combinedData.blockingType);
                              const hasNewTasks = combinedData.hasNewTasks;

                              return (
                                <Tooltip
                                  key={day.iso}
                                  title={
                                    <Box>
                                      <Typography variant="caption" fontWeight={600} display="block">
                                        {day.dayName} {day.dayNum}
                                      </Typography>
                                      {existingData.tasks.length > 0 && (
                                        <>
                                          <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 0.5 }}>Bestaand:</Typography>
                                          {existingData.tasks.map((task, i) => (
                                            <Typography key={i} variant="caption" display="block" sx={{ opacity: 0.9 }}>
                                              • {task.name} ({task.hours || 8}u)
                                            </Typography>
                                          ))}
                                        </>
                                      )}
                                      {hasNewTasks && (
                                        <>
                                          <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 0.5, color: '#60a5fa' }}>+ Nieuw:</Typography>
                                          <Typography variant="caption" display="block" sx={{ color: '#60a5fa' }}>
                                            • Nieuwe taak ({combinedData.newHours}u)
                                          </Typography>
                                        </>
                                      )}
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>
                                        Totaal: {combinedData.hours.toFixed(1)}u / 8u = {combinedData.percentage}%
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      width: dayWidth,
                                      minWidth: dayWidth,
                                      height: 32,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: style.bg,
                                      borderRight: '1px solid rgba(255,255,255,0.3)',
                                      cursor: 'default',
                                      // Visual indicator for new tasks
                                      ...(hasNewTasks && {
                                        boxShadow: 'inset 0 0 0 2px #3b82f6',
                                      }),
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        color: style.text,
                                      }}
                                    >
                                      {style.label}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </Box>

                        {/* Expanded task list */}
                        <Collapse in={isExpanded}>
                          <Box sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e5e7eb' }}>
                            {personTasks.length > 0 ? (
                              groupTasksForDisplay(personTasks, workingDays).map((item, idx, arr) => {
                                // Handle grouped tasks (like Vanallesuurtje)
                                if (item.isGroup) {
                                  const groupColor = getTaskColor(item);
                                  return (
                                    <Box
                                      key={item.id}
                                      sx={{
                                        display: 'flex',
                                        borderBottom: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: sidebarWidth,
                                          minWidth: sidebarWidth,
                                          borderRight: '1px solid #e5e7eb',
                                          p: 0.5,
                                          pl: 4,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: 1,
                                            backgroundColor: groupColor.bg,
                                            flexShrink: 0,
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          sx={{ fontSize: '0.65rem', color: '#6b7280' }}
                                        >
                                          {item.name} ({item.tasks.length}x)
                                        </Typography>
                                      </Box>
                                      <Box sx={{ flex: 1, position: 'relative', minHeight: 24, display: 'flex' }}>
                                        {workingDays.map(day => {
                                          // Check if any task in the group is on this day
                                          const tasksOnDay = item.tasks.filter(t => {
                                            const taskDays = getTaskDaysMap(t, workingDays);
                                            return taskDays[day.iso];
                                          });
                                          const totalHours = tasksOnDay.reduce((sum, t) => sum + (t.hours || 1), 0);

                                          return (
                                            <Box
                                              key={day.iso}
                                              sx={{
                                                width: dayWidth,
                                                minWidth: dayWidth,
                                                height: 24,
                                                backgroundColor: tasksOnDay.length > 0 ? groupColor.bg : 'transparent',
                                                borderRight: '1px solid rgba(255,255,255,0.3)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                              }}
                                            >
                                              {tasksOnDay.length > 0 && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.55rem',
                                                    color: groupColor.text,
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  {totalHours}u
                                                </Typography>
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  );
                                }

                                // Regular task
                                const task = item;
                                const taskColor = getTaskColor(task);
                                const taskDays = getTaskDaysMap(task, workingDays);
                                const taskDayCount = Object.keys(taskDays).length;

                                return (
                                  <Box
                                    key={task.id || idx}
                                    sx={{
                                      display: 'flex',
                                      borderBottom: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    }}
                                  >
                                    {/* Task name */}
                                    <Box
                                      sx={{
                                        width: sidebarWidth,
                                        minWidth: sidebarWidth,
                                        borderRight: '1px solid #e5e7eb',
                                        p: 0.5,
                                        pl: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: 1,
                                          backgroundColor: taskColor.bg,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{ fontSize: '0.65rem', color: '#6b7280' }}
                                        title={task.name}
                                      >
                                        {task.name}
                                      </Typography>
                                    </Box>

                                    {/* Task bar spanning days */}
                                    <Box sx={{ flex: 1, position: 'relative', minHeight: 24, display: 'flex' }}>
                                      {workingDays.map(day => {
                                        const isTaskDay = taskDays[day.iso];

                                        return (
                                          <Box
                                            key={day.iso}
                                            sx={{
                                              width: dayWidth,
                                              minWidth: dayWidth,
                                              height: 24,
                                              backgroundColor: isTaskDay ? taskColor.bg : 'transparent',
                                              borderRight: '1px solid rgba(255,255,255,0.3)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            {isTaskDay && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontSize: '0.55rem',
                                                  color: taskColor.text,
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {Math.round((task.hours || 8) / taskDayCount)}u
                                              </Typography>
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                );
                              })
                            ) : (
                              <Box sx={{ p: 1, pl: 4 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Geen taken in deze periode
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Geen {role.toLowerCase()}s gevonden
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Legend */}
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Legenda:</Typography>
          {[
            { label: 'Draaidag', color: taskTypeColors['Draaidag'] },
            { label: 'Vrije dag', color: taskTypeColors['Vrije dag'] },
            { label: 'Montage', color: taskTypeColors['Montage'] },
            { label: 'Animatie', color: taskTypeColors['Animatie'] },
            { label: 'Overig', color: taskTypeColors['default'] },
            { label: 'Feedback', color: { bg: '#e5e7eb' }, isFeedback: true },
          ].map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                backgroundColor: item.isFeedback
                  ? 'repeating-linear-gradient(45deg, #e5e7eb, #e5e7eb 2px, #f3f4f6 2px, #f3f4f6 4px)'
                  : item.color.bg,
                border: item.isFeedback ? '1px dashed #9ca3af' : 'none',
              }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
        {!allAssigned && (
          <Alert severity="info" sx={{ flex: 1, mr: 2, py: 0 }}>
            Selecteer een persoon voor elke rol
          </Alert>
        )}
        <Button
          onClick={handleAddToCalendar}
          disabled={calendarLoading}
          startIcon={calendarLoading ? <CircularProgress size={16} /> : <CalendarMonthIcon />}
          sx={{ mr: 'auto', textTransform: 'none' }}
          size="small"
        >
          Naar Outlook
        </Button>
        <Button onClick={onClose}>Annuleren</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!allAssigned}
          startIcon={<CheckCircleIcon />}
          sx={{ borderRadius: 2 }}
        >
          Toepassen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PlanningPuzzleDialog;
